<template>
  <section class="login register">
    <div class="login-wrapper">
      <div class="fp-logo">
        <img src="@/assets/images/logo.png" alt="FamiliPay logo">
      </div>
      <div class="login-form-wrapper box">
        <p class="small-text uppercase-text">{{ $lang.messages.welcome }}</p>
        <h1 class="title uppercase-text">{{ $lang.messages.register }}</h1>
        <hr class="horizontal-line">
        <form
          class="login-form"
          name="registerForm"
          novalidate
          @submit.prevent
        >
          <input name="hidden" type="text" style="display:none;">
          
          <!-- FIRST NAME -->
          <p class="input-filter-label">{{ $lang.messages.firstName }}</p>
          <div class="input-wrapper input-field" :class="{'error-input' : errors.has('firstname')}">
            <input
              type="text"
              class="input"
              :placeholder="$lang.messages.enterFirstName"
              name="firstname"
              v-model="firstname"
              required
              v-validate
              @focus.self="clearInputErrorsChild"
              @blur="clearOthersChild"
            >
            <img src="@/assets/images/validation_ico.png" alt="" class="icon">
            <div
              v-show="errors.has('firstname')"
              class="error-tooltip"
              :class="{'hide-tooltip': bluredPropChild !== 'firstname'}"
            >
              <div class="text">
                {{ errors.first('firstname') }}
              </div>
            </div>
          </div>
          
          <!-- LAST NAME -->
          <p class="input-filter-label">{{ $lang.messages.lastName }}</p>
          <div class="input-wrapper input-field" :class="{'error-input' : errors.has('lastname')}">
            <input
              type="text"
              class="input"
              :placeholder="$lang.messages.enterLastName"
              name="lastname"
              v-model="lastname"
              required
              v-validate
              @focus.self="clearInputErrorsChild"
              @blur="clearOthersChild"
            >
            <img src="@/assets/images/validation_ico.png" alt="" class="icon">
            <div class="error-tooltip" v-show="errors.has('lastname')" :class="{'hide-tooltip' : bluredPropChild !== 'lastname'}">
              <div class="text">
                {{ errors.first('lastname') }}
              </div>
            </div>
          </div>
          
          <!-- EMAIL -->
          <template v-if="!token">
            <f-p-input-email
              :isRegister="true"
              :bluredProp="bluredProp"
              :emailProp="emailProp"
              @updateEmail="updateEmail"
              @clearOthersEmail="clearOthers">
            </f-p-input-email>
          </template>
          
          <!-- PASSWORD & REPEAT PASSWORD -->
          <f-p-input-new-repeat-pass
            :isRegister="true"
            :bluredProp="bluredProp"
            :passwordProp="passwordProp"
            :repeatPasswordProp="repeatPasswordProp"
            @updatePass="updatePass"
            @updateRepeatPass="updateRepeatPass"
            @clearOthersPass="clearOthers"
            @clearBluredFromChild="checkAnyInput"
            @isCapsOn="isCapsOn">
          </f-p-input-new-repeat-pass>
          
          <!-- COUNTRY -->
          <div class="short-wrapper">
            <p class="input-filter-label">{{ $lang.messages.country }}</p>
            <div class="wrapper">
              <autocomplete
                v-if="countries.length !== 0"
                :items="countries"
                :option="country"
                :placeholder="$lang.messages.enterCountry"
                :name="'countryCode'"
                :bluredProp="bluredPropChild"
                :bigSize="true"
                @optionChanged="onSelectedCountry"
                @clearInputErrors="clearInputErrorsChild"
                @clearOthers="clearOthersChild"
                ref="autocompleteCountry"
              />
            </div>
          </div>
  
          <!-- PHONE NUMBER -->
          <div class="long-wrapper">
            <p class="input-filter-label">{{ $lang.messages.phoneNumber }}</p>
            <div class="input-wrapper input-field" :class="{'error-input' : errors.has('phoneNumber')}">
              <input
                  type="number"
                  class="input"
                  :placeholder="$lang.messages.enterPhoneNumber"
                  name="phoneNumber"
                  v-model="phoneNumber"
                  required
                  v-validate="'required|min:1|max:15'"
                  :min="0"
                  @input="updatePhoneNumber"
                  @focus.self="clearInputErrorsChild"
                  @blur="clearOthersChild"
                  @keypress="MX_preventCharInputWithoutZero"
              >
              <img src="@/assets/images/validation_ico.png" alt="" class="icon">
              <div
                  v-show="errors.has('phoneNumber')"
                  class="error-tooltip"
                  :class="{'hide-tooltip': bluredPropChild !== 'phoneNumber'}"
              >
                <div class="text">
                  {{ errors.first('phoneNumber') }}
                </div>
              </div>
            </div>
          </div>
  
          <!-- REFERRAL CODE -->
          <p class="input-filter-label">{{ $lang.messages.referralCode }}</p>
          <div class="input-wrapper input-field">
            <input
              type="text"
              class="input"
              :placeholder="$lang.messages.enterReferralCode"
              name="referralCode"
              v-model="referralCode"
              @focus.self="clearInputErrorsChild"
              @blur="clearOthersChild"
            >
          </div>
          
          <!-- CAPS LOCK HANDLER -->
          <f-p-is-caps-on :capsOnText="$lang.messages.capsIsOn" :isCapsOn="capsOn"></f-p-is-caps-on>
          
          <p class="input-filter-label">
            {{ $lang.messages.iAgreeLabelFirst }}
            <a href="https://familipay.com/terms-of-service/" target="blank">{{ $lang.messages.termsOfService }}</a>
            {{ $lang.messages.and }}
            <a href="https://familipay.com/privacy/" target="blank">{{ $lang.messages.privacyPolicy }}</a>
          </p>
          <div class="checkbox-div" :class="{'error-input' : errors.has('acceptance')}">
            <label class="checkbox-wrapper" :class="{'error' : errors.has('acceptance')}">
              {{ $lang.messages.iAgree }}
              <input type="checkbox" v-model="acceptance" @click="onAcceptanceClicked($event)">
              <span class="checkmark" :class="{'error' : errors.has('acceptance')}"></span>
            </label>
            <img src="@/assets/images/validation_ico.png" alt="" class="icon"
                 v-show="errors.has('acceptance')">
            <div
              class="error-tooltip"
              v-show="errors.has('acceptance')"
              :class="{'hide-tooltip': bluredPropChild !== 'acceptance'}"
            >
              <div class="text">
                {{ errors.first('acceptance') }}
              </div>
            </div>
          </div>
  
          <div class="recaptcha" :class="{'error' : !isRecaptchaValid}" v-if="showRecaptcha">
            <div id="g-recaptcha" class="g-recaptcha" :data-sitekey="sitekey"></div>
          </div>

          <!-- SUBMIT -->
          <f-p-input-button
            :buttonText="$lang.messages.registerAccount"
            :loading="loading"
            @formSubmit="onRegisterFormSubmitted">
          </f-p-input-button>
          
        </form>
        
        <hr class="horizontal-line">
  
        <f-p-input-footer
          :regLabel="$lang.messages.ifYouHaveAccount"
          :leadsTo="'Login'"
          :regDesc="$lang.messages.loginHere">
        </f-p-input-footer>
        
      </div>
      
      <f-p-copyright
        :copLabel="$lang.messages.copyright"
        :copDesc="$lang.messages.familipay">
      </f-p-copyright>
      
    </div>
    <div class="footer register">
      <img src="@/assets/images/registration_illustration.png" class="registration-illustration">
    </div>
  </section>
</template>

<script>
  import { mapActions } from "vuex";
  import { REGISTER, REGISTER_CONFIRM, GET_COUNTRIES } from "@/store/action.types";
  import Autocomplete from "@/components/common/Autocomplete";
  import { showHidePassword} from "@/mixins/showHidePassword";
  import FPInputEmail from "@/components/common/inputs/FPInputEmail";
  import FPInputNewRepeatPass from "@/components/common/inputs/FPInputNewRepeatPass";
  import FPInputFooter from "@/components/common/inputs/FPInputFooter";
  import FPCopyright from "@/components/common/inputs/FPCopyright";
  import FPInputButton from "@/components/common/inputs/FPInputButton";
  import FPIsCapsOn from "@/components/common/inputs/FPIsCapsOn";
  import { APP_RECAPTCHA_SITE_KEY } from '@/common/config';
  import { preventCharEnterWithoutZero } from "@/mixins/preventCharEnterWithoutZero";

  export default {
    name: "registration",
    components: {
      Autocomplete,
      'f-p-input-email': FPInputEmail,
      'f-p-input-footer': FPInputFooter,
      'f-p-copyright': FPCopyright,
      'f-p-input-new-repeat-pass': FPInputNewRepeatPass,
      'f-p-input-button': FPInputButton,
      'f-p-is-caps-on': FPIsCapsOn
    },
    mixins: [
      showHidePassword,
      preventCharEnterWithoutZero,
    ],
    props: {
      bluredProp: {
        type: String,
        required: true
      },
      emailProp: {
        type: String,
        required: true
      },
      passwordProp: {
        type: String,
        required: true
      },
      repeatPasswordProp: {
        type: String,
        required: true
      },
      loading: {
        type: Boolean,
        required: true
      },
      capsOn: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        firstname: '',
        lastname: '',
        country: {
          code: '',
          name: ''
        },
        phoneNumber: '',
        referralCode: '',
        countries: [],
        token: '',
        formSubmitted: false,
        acceptance: false,
        bluredPropChild: '',
        recaptchaToken: '',
        isRecaptchaValid: true,
        showRecaptcha: process.env.VUE_APP_DEBUG,
        sitekey: APP_RECAPTCHA_SITE_KEY
      }
    },
    mounted() {
      this.showRecaptcha = this.showRecaptcha.split(',')[0] !== 'true';
      this.loadCountries();
      this.token = this.$route.query.token;
      if (this.token !== undefined) {
        this.$snotify.success(this.$lang.messages.sn_invitationAccepted, 'SUCCESS');
      }
      if (this.showRecaptcha) {
        grecaptcha.render('g-recaptcha', {
          'sitekey' : this.sitekey,
          'callback' : this.validateRecaptcha()
        });
      }
    },
    methods: {
      ...mapActions({
        registerAction: REGISTER,
        registerConfirmAction: REGISTER_CONFIRM,
        getCountriesAction: GET_COUNTRIES
      }),
      onRegisterFormSubmitted() {
        // On form submit remove the error toolip, but leave the error on input field
        this.$emit('clearInputs');
        this.checkAnyInput();
  
        if (this.showRecaptcha) {
          let response = grecaptcha.getResponse();
          this.validateRecaptcha(response);
        }
        
        this.$validator.validateAll().then(result => {
          this.formSubmitted = true;
          
          this.setMarginTooltip();
          
          if (this.country.name === '') {
            this.$validator.errors.add({
              field: "country",
              msg: `${this.$lang.messages.vee_selectValidField} ${this.$lang.messages.country.toLowerCase()}.`
            });
            this.$refs.autocompleteCountry.setValidateField()
          }
          
          if (!this.acceptance) {
            this.$validator.errors.add({
              field: "acceptance",
              msg: this.$lang.messages.vee_tos
            });
          }
          
          if (this.showRecaptcha) {
            if (this.recaptchaToken === '') {
              this.isRecaptchaValid = false;
            }
          } else {
            this.isRecaptchaValid = true;
          }
          
          if (result && this.acceptance && this.isRecaptchaValid) {
            // On form submit show the loading spinner
            this.$emit('isLoading', true);
            
            if (this.token === '' || this.token === undefined) {
              this.registerAction({
                firstname: this.firstname,
                lastname: this.lastname,
                email: this.emailProp,
                password: this.passwordProp,
                repeatPassword: this.repeatPassword,
                countryCode: this.country.code,
                phoneNumber: this.country.phoneNumberPrefix.substring(1, this.country.phoneNumberPrefix.length - 1) + this.phoneNumber,
                referralCode: this.referralCode.length > 0 ? this.referralCode : null,
                acceptance: this.acceptance,
                recaptchaToken: this.showRecaptcha ? this.recaptchaToken : null
              })
                .then(() => {
                  this.$snotify.success(this.$lang.messages.sn_confirmReg, 'SUCCESS');
                  this.$router.push({ name: "PhoneNumberVerification" })
                })
                .catch(error => {
                  this.recaptchaToken = '';
                  if (this.showRecaptcha) {
                    grecaptcha.reset();
                  }
                  this.$emit('isLoading', false);
                  this.$snotify.error(error.data.status.desc, 'ERROR')
                });
            } else {
              this.registerConfirmAction({
                token: this.token,
                firstname: this.firstname,
                lastname: this.lastname,
                password: this.passwordProp,
                repeatPassword: this.repeatPassword,
                phoneNumber: this.country.phoneNumberPrefix.substring(1, this.country.phoneNumberPrefix.length - 1) + this.phoneNumber,
                countryCode: this.country.code,
                referralCode: this.referralCode.length > 0 ? this.referralCode : null,
                acceptance: this.acceptance,
                recaptchaToken: this.showRecaptcha ? this.recaptchaToken : null
              })
              .then(response => {
                this.$snotify.success(this.$lang.messages.sn_confirmReg, 'SUCCESS');
                this.$router.push({ name: "PhoneNumberVerification" })
                this.updateEmail(response.data.data.email);
              })
              .catch(error => {
                this.recaptchaToken = '';
                if (this.showRecaptcha) {
                  grecaptcha.reset();
                }
                this.$emit('isLoading', false);
                this.$snotify.error(error.data.status.desc, 'ERROR')
              });
            }
          }
        });
      },
      loadCountries() {
        this.countries.push(
          {
            code: 'US',
            name: 'USA (+1)',
            phoneNumberPrefix: '(+1)'
          }
        );
        this.country = this.countries.find(function(element) {
          return element.code === 'US'
        });
      },
      onSelectedCountry(value) {
        let countryValid = false;
        this.countries.forEach(element => {
          if (element.code === value.code) {
            countryValid = true
          }
        });
        if (countryValid === true) {
          this.country = value
        } else {
          this.country = {}
        }
      },
      setMarginTooltip() {
        let errorTooltip = document.getElementsByClassName('error-tooltip');
        
        for (let item of errorTooltip) {
          let height = item.offsetHeight;
          if (height >= 60) {
            item.style.marginTop = "-40%"
          } else {
            item.style.marginTop = "-85px"
          }
        }
      },
      clearInputErrorsChild(e) {
        // Remove bluredProp from LandingLayout
        this.$emit('clearBluredPropLandingLayout');
        
        if (e.target) {
          this.$validator.errors.remove(e.target.name)
        } else {
          this.$validator.errors.remove(e)
        }
      },
      checkAnyInput() {
        this.bluredPropChild = ''
      },
      clearOthersChild(e) {
        this.bluredPropChild = e.target.name
      },
      onAcceptanceClicked() {
        this.acceptance = !this.acceptance;
        if (this.acceptance) {
          this.$validator.errors.remove('acceptance')
        } else {
          this.$validator.errors.add({
            field: "acceptance",
            msg: this.$lang.messages.vee_tos
          });
        }
      },
      // Listen to values inside FPInputEmail, call a function
      // and update the prop parent (LandingLayout) is sending to FPInputEmail
      updateEmail (val) {
        this.$emit('updateEmail', val)
      },
      // Listen to values inside FPInputNewPass, call a function
      // and update the prop parent (LandingLayout) is sending to FPInputNewPass
      updatePass (val) {
        this.$emit('updatePass', val)
      },
      // Listen to values inside FPInputRepeatPass, call a function
      // and update the prop parent (LandingLayout) is sending to FPInputRepeatPass
      updateRepeatPass (val) {
        this.$emit('updateRepeatPass', val)
      },
      // Listen to values inside input for phone number, call a function
      // and update the prop parent (LandingLayout)
      updatePhoneNumber() {
        this.$parent.updatePhoneNumber(this.country.phoneNumberPrefix.substring(1, this.country.phoneNumberPrefix.length - 1) + this.phoneNumber);
      },
      // Clear input errors from children, and send value to LandingLayout
      clearOthers (val) {
        this.$emit('clearOthers', val)
      },
      // Turn caps on/off from child
      isCapsOn (val) {
        this.$emit('isCapsOn', val)
      },
      validateRecaptcha(response) {
        this.isRecaptchaValid = true;
        this.recaptchaToken = response;
      }
    }
  };
</script>
